import { useMutation, UseMutationResult, useQueryClient } from "@tanstack/react-query";
import {
  IProductTemplate,
  ITableDataEntryModified,
  ITableDataEntry,
  ITableDataEntryValue,
} from "components/dashboard/product-template-table/utils/interfaces";
import { APIResponse } from "services/HttpService";
import { ProductTemplatesApi } from "../ProductTemplatesApi";
import {
  defaultMutationOptions,
  defaultResponseHandler,
  IMutationOptions,
  IQueryOptions,
  QueryKey,
  useQueryWrapper,
} from "./api-hooks-common";

export type ProductTemplateEntryType = {
  safeName: string;
  dateFrom?: string;
  dateTo?: string;
  filter?: string;
};

export const useGetProductTemplateInfos = () =>
  useQueryWrapper([QueryKey.PRODUCT_TEMPLATE_INFOS], () =>
    ProductTemplatesApi.getProductTemplateInfos()
  );
export const useGetProductTemplateInfo = (id: number | null, options?: IQueryOptions) =>
  useQueryWrapper(
    [QueryKey.PRODUCT_TEMPLATE_INFOS, id?.toString()],
    () => ProductTemplatesApi.getProductTemplateInfo(id),
    null,
    options
  );

export const useGetProductTemplateConfig = (
  refName: string,
  id: string,
  hiddenColumns?: string[],
  onlyShownColumns?: string[],
  options?: {
    usePromiseHandler?: ((res: APIResponse<IProductTemplate>) => any) | null | undefined;
    enabled?: boolean;
  }
) =>
  useQueryWrapper(
    [
      QueryKey.PRODUCT_TEMPLATE_CONFIGS,
      refName,
      id,
      hiddenColumns ? JSON.stringify(hiddenColumns) : undefined,
      onlyShownColumns ? JSON.stringify(onlyShownColumns) : undefined,
    ],
    () => ProductTemplatesApi.getProductTemplateConfig(refName),
    options?.usePromiseHandler ?? (res => res.data),
    options
  );

export const useGetProductTemplateData = (
  params: ProductTemplateEntryType,
  options?: IQueryOptions
) =>
  useQueryWrapper(
    [
      QueryKey.PRODUCT_TEMPLATE_DATA,
      params.safeName,
      params.dateFrom,
      params.dateTo,
      params.filter,
    ],
    () => ProductTemplatesApi.getProductTemplateData(params),
    null,
    options
  );

export const useGetProductTemplateFilterPreviewData = (
  params: ProductTemplateEntryType,
  options?: IQueryOptions
) =>
  useQueryWrapper(
    [
      QueryKey.PRODUCT_TEMPLATE_PREVIEW_FILTER_DATA,
      params.safeName,
      params.dateFrom,
      params.dateTo,
      params.filter,
    ],
    () => ProductTemplatesApi.getProductTemplateDataFilterPreview(params),
    null,
    options
  );

export const useGetProductTemplateForeignKeys = (templateName: string, options?: IQueryOptions) =>
  useQueryWrapper(
    [QueryKey.PRODUCT_TEMPLATE_FOREIGN_KEYS, templateName],
    () => ProductTemplatesApi.getProductTemplateForeignKeys(templateName),
    null,
    options
  );

export function useUpdateProductTemplateEntry(
  params: ProductTemplateEntryType,
  specOptions?: IMutationOptions
): UseMutationResult<
  APIResponse<any>,
  unknown,
  {
    entryId: string;
    body: any;
  }
> {
  const options = { ...defaultMutationOptions, ...specOptions };
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { entryId: string; body: any }) =>
      ProductTemplatesApi.updateProductTemplateEntry({ safeName: params.safeName, ...data }).then(
        defaultResponseHandler
      ),
    onError: error => {
      options.onFailure!!(error);
    },
    onMutate: variables => {
      options.onMutate!!(variables);
    },
    onSuccess: data => {
      queryClient.setQueryData(
        [
          QueryKey.PRODUCT_TEMPLATE_DATA,
          params.safeName,
          params.dateFrom,
          params.dateTo,
          params.filter,
        ],
        (oldQueryData: any) => {
          return oldQueryData.map((obj: any) => {
            if (obj.id === data.id) {
              return data;
            }
            return obj;
          });
        }
      );
      options.onSuccess!!();
    },
  });
}

export function useDeleteProductTemplateEntry(
  params: ProductTemplateEntryType,
  specOptions?: IMutationOptions
): UseMutationResult<
  APIResponse<any>,
  unknown,
  {
    entryId: string;
  }
> {
  const options = { ...defaultMutationOptions, ...specOptions };
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { entryId: string }) =>
      ProductTemplatesApi.deleteProductTemplateEntry(params.safeName, data.entryId).then(
        defaultResponseHandler
      ),
    onSuccess: data => {
      queryClient.setQueryData(
        [
          QueryKey.PRODUCT_TEMPLATE_DATA,
          params.safeName,
          params.dateFrom,
          params.dateTo,
          params.filter,
        ],
        (oldQueryData: any) => {
          return oldQueryData.filter((e: ITableDataEntry) => e.id !== data.id);
        }
      );

      options.onSuccess!!();
    },
    onError: error => {
      options.onFailure!!(error);
    },
    onMutate: variables => {
      options.onMutate!!(variables);
    },
  });
}

export function useCreateProductTemplateEntry(
  params: ProductTemplateEntryType,
  specOptions?: IMutationOptions
): UseMutationResult<
  ITableDataEntry | undefined,
  unknown,
  {
    body: ITableDataEntryValue[];
  }
> {
  const options = { ...defaultMutationOptions, ...specOptions };
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { body: ITableDataEntryValue[] }) =>
      ProductTemplatesApi.createProductTemplateEntry(params.safeName, data.body).then(
        defaultResponseHandler<ITableDataEntry>
      ),
    onSuccess: data => {
      queryClient.setQueryData(
        [
          QueryKey.PRODUCT_TEMPLATE_DATA,
          params.safeName,
          params.dateFrom,
          params.dateTo,
          params.filter,
        ],
        (oldQueryData: ITableDataEntry[]) => {
          return [data, ...oldQueryData];
        }
      );
      options.onSuccess!!();
    },
    onError: error => {
      options.onFailure!!(error);
    },
    onMutate: variables => {
      options.onMutate!!(variables);
    },
  });
}

export function useCreateProductTemplateEntries(
  params: ProductTemplateEntryType,
  specOptions?: IMutationOptions
): UseMutationResult<ITableDataEntry[] | undefined, unknown, ITableDataEntryModified[]> {
  const options = { ...defaultMutationOptions, ...specOptions };
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ITableDataEntryModified[]) =>
      ProductTemplatesApi.createProductTemplateEntries(body).then(
        defaultResponseHandler<ITableDataEntry[]>
      ),
    onSuccess: data => {
      queryClient.setQueryData(
        [
          QueryKey.PRODUCT_TEMPLATE_DATA,
          params.safeName,
          params.dateFrom,
          params.dateTo,
          params.filter,
        ],
        (oldQueryData: ITableDataEntry[]) => {
          if (!data) return oldQueryData;
          return [...data, ...oldQueryData];
        }
      );
      options.onSuccess!!();
    },
    onError: error => {
      options.onFailure!!(error);
    },
  });
}

export function useSignProductTemplateEntry(
  params: ProductTemplateEntryType,
  specOptions?: IMutationOptions
): UseMutationResult<
  APIResponse<any>,
  unknown,
  {
    entryId: string;
    referenceName: string;
  }
> {
  const options = { ...defaultMutationOptions, ...specOptions };
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (signData: { entryId: string; referenceName: string }) =>
      ProductTemplatesApi.signProductTemplateEntry(
        params.safeName,
        signData.entryId,
        signData.referenceName
      ).then(response => {
        const responseDataWithSignData: any = {
          ...response,
          entryId: signData.entryId,
          referenceName: signData.referenceName,
        };
        return responseDataWithSignData;
      }),

    onSuccess: data => {
      queryClient.setQueryData(
        [
          QueryKey.PRODUCT_TEMPLATE_DATA,
          params.safeName,
          params.dateFrom,
          params.dateTo,
          params.filter,
        ],
        (oldQueryData: any) => {
          return oldQueryData.map((obj: any) => {
            if (obj.id === data.entryId) {
              if (obj.entries[data.referenceName] != null) {
                obj.entries[data.referenceName].value = data.data;
              }
            }
            return obj;
          });
        }
      );
      options.onSuccess!!(data);
    },
    onError: error => {
      options.onFailure!!(error);
    },
    onMutate: variables => {
      options.onMutate!!(variables);
    },
  });
}

export function useDeleteProductTemplateEntriesByFilter(
  params: ProductTemplateEntryType,
  specOptions?: IMutationOptions
): UseMutationResult<
  APIResponse<any>,
  unknown,
  {
    dateFrom?: string;
    dateTo?: string;
    filter?: string;
  }
> {
  const options = { ...defaultMutationOptions, ...specOptions };
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: data =>
      ProductTemplatesApi.deleteProductTemplateEntriesByFilter(params.safeName, data).then(
        defaultResponseHandler
      ),
    onSuccess: () => {
      queryClient.setQueryData(
        [
          QueryKey.PRODUCT_TEMPLATE_PREVIEW_FILTER_DATA,
          params.safeName,
          params.dateFrom,
          params.dateTo,
          params.filter,
        ],
        () => {
          return [];
        }
      );

      options.onSuccess!!();
    },
    onError: error => {
      options.onFailure!!(error);
    },
    onMutate: variables => {
      options.onMutate!!(variables);
    },
  });
}

export function useWipeAllProductTemplateEntries(
  params: ProductTemplateEntryType,
  specOptions?: IMutationOptions
): UseMutationResult<APIResponse<any>, unknown, unknown> {
  const options = { ...defaultMutationOptions, ...specOptions };
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      ProductTemplatesApi.wipeAllProductTemplateEntries(params.safeName).then(
        defaultResponseHandler
      ),
    onSuccess: () => {
      queryClient.setQueryData(
        [
          QueryKey.PRODUCT_TEMPLATE_PREVIEW_FILTER_DATA,
          params.safeName,
          params.dateFrom,
          params.dateTo,
          params.filter,
        ],
        () => {
          return [];
        }
      );

      options.onSuccess!!();
    },
    onError: error => {
      options.onFailure!!(error);
    },
    onMutate: variables => {
      options.onMutate!!(variables);
    },
  });
}
